import React from 'react';
import { motion } from 'framer-motion';
import { 
  ExclamationTriangleIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  GlobeAltIcon,
  ClockIcon,
  ShieldExclamationIcon,
  InformationCircleIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const RiskDisclosure = () => {
  const lastUpdated = "January 18, 2025";

  const riskCategories = [
    {
      id: "market-risk",
      title: "Market Risk",
      icon: ChartBarIcon,
      color: "red",
      risks: [
        {
          name: "Price Volatility",
          description: "Financial instruments can experience significant price fluctuations due to market conditions, economic events, and investor sentiment. These price movements can result in substantial gains or losses."
        },
        {
          name: "Market Liquidity",
          description: "Some markets may have limited liquidity, making it difficult to execute trades at desired prices or exit positions quickly, especially during volatile market conditions."
        },
        {
          name: "Market Hours",
          description: "Trading outside regular market hours may involve wider spreads, reduced liquidity, and increased volatility, which can impact execution prices and increase trading costs."
        }
      ]
    },
    {
      id: "leverage-risk",
      title: "Leverage and Margin Risk",
      icon: CurrencyDollarIcon,
      color: "orange",
      risks: [
        {
          name: "Amplified Losses",
          description: "Leverage amplifies both potential profits and losses. You may lose more than your initial investment when trading on margin or using leveraged products."
        },
        {
          name: "Margin Calls",
          description: "If your account equity falls below required margin levels, you may receive a margin call requiring additional funds or position closure, potentially at unfavorable prices."
        },
        {
          name: "Forced Liquidation",
          description: "We may automatically close your positions if margin requirements are not met, potentially resulting in significant losses regardless of market conditions."
        }
      ]
    },
    {
      id: "operational-risk",
      title: "Operational and Technology Risk",
      icon: ClockIcon,
      color: "blue",
      risks: [
        {
          name: "System Failures",
          description: "Technical failures, system outages, or connectivity issues may prevent you from accessing your account, placing orders, or managing positions when needed."
        },
        {
          name: "Execution Delays",
          description: "Order execution may be delayed due to system processing, market conditions, or high trading volumes, potentially resulting in unfavorable execution prices."
        },
        {
          name: "Data Accuracy",
          description: "Market data and platform information may be delayed, inaccurate, or incomplete, which could affect your trading decisions and outcomes."
        }
      ]
    },
    {
      id: "regulatory-risk",
      title: "Regulatory and Legal Risk",
      icon: ShieldExclamationIcon,
      color: "purple",
      risks: [
        {
          name: "Regulatory Changes",
          description: "Changes in laws, regulations, or tax policies may affect the availability, cost, or profitability of trading activities and investment strategies."
        },
        {
          name: "Jurisdictional Issues",
          description: "Different jurisdictions may have varying levels of investor protection, regulatory oversight, and legal recourse for trading activities."
        },
        {
          name: "Compliance Requirements",
          description: "You are responsible for understanding and complying with all applicable laws and regulations in your jurisdiction regarding trading and investment activities."
        }
      ]
    },
    {
      id: "currency-risk",
      title: "Currency and International Risk",
      icon: GlobeAltIcon,
      color: "green",
      risks: [
        {
          name: "Exchange Rate Risk",
          description: "When trading instruments denominated in foreign currencies, exchange rate fluctuations can significantly impact the value of your investments."
        },
        {
          name: "Political Risk",
          description: "Political instability, government actions, or changes in international relations can affect market conditions and investment values."
        },
        {
          name: "Time Zone Differences",
          description: "Trading in international markets may require monitoring positions during different time zones, potentially limiting your ability to respond to market events."
        }
      ]
    }
  ];

  const colorClasses = {
    red: {
      bg: "bg-red-50",
      border: "border-red-200",
      text: "text-red-900",
      icon: "text-red-600"
    },
    orange: {
      bg: "bg-orange-50",
      border: "border-orange-200", 
      text: "text-orange-900",
      icon: "text-orange-600"
    },
    blue: {
      bg: "bg-blue-50",
      border: "border-blue-200",
      text: "text-blue-900", 
      icon: "text-blue-600"
    },
    purple: {
      bg: "bg-purple-50",
      border: "border-purple-200",
      text: "text-purple-900",
      icon: "text-purple-600"
    },
    green: {
      bg: "bg-green-50",
      border: "border-green-200",
      text: "text-green-900",
      icon: "text-green-600"
    }
  };

  return (
    <div className="min-h-screen bg-white pt-24 pb-16">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-xl flex items-center justify-center">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
            </div>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Risk Disclosure
          </h1>
          <p className="text-xl text-gray-600 mb-4">
            Important information about trading risks and potential losses
          </p>
          <p className="text-sm text-gray-500">
            Last updated: {lastUpdated}
          </p>
        </motion.div>

        {/* Critical Warning */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-red-100 border-2 border-red-300 rounded-lg p-6 mb-8"
        >
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-600 mr-4 mt-1 flex-shrink-0" />
            <div>
              <h2 className="text-2xl font-bold text-red-900 mb-3">⚠️ CRITICAL RISK WARNING</h2>
              <div className="space-y-3 text-red-800">
                <p className="font-semibold text-lg">
                  Trading financial instruments involves substantial risk of loss and is not suitable for all investors.
                </p>
                <p>
                  You should carefully consider whether trading is appropriate for you in light of your experience, 
                  objectives, financial resources, and other relevant circumstances. You may lose some or all of 
                  your invested capital, so you should not invest money that you cannot afford to lose.
                </p>
                <p className="font-semibold">
                  Past performance is not indicative of future results. All trading involves risk.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Risk Categories */}
        <div className="space-y-8">
          {riskCategories.map((category, index) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + index * 0.1 }}
              className={`${colorClasses[category.color].bg} ${colorClasses[category.color].border} border rounded-lg p-6`}
            >
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center mr-3 shadow-sm">
                  <category.icon className={`h-6 w-6 ${colorClasses[category.color].icon}`} />
                </div>
                <h2 className={`text-2xl font-bold ${colorClasses[category.color].text}`}>
                  {category.title}
                </h2>
              </div>
              
              <div className="space-y-4">
                {category.risks.map((risk, idx) => (
                  <div key={idx} className="bg-white bg-opacity-60 rounded-lg p-4">
                    <h3 className={`text-lg font-semibold ${colorClasses[category.color].text} mb-2`}>
                      {risk.name}
                    </h3>
                    <p className="text-gray-700 leading-relaxed">{risk.description}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Specific Product Risks */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mt-8"
        >
          <h2 className="text-2xl font-bold text-yellow-900 mb-4">Specific Product Risks</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white bg-opacity-60 rounded-lg p-4">
              <h3 className="font-semibold text-yellow-800 mb-2">Cryptocurrency Trading</h3>
              <p className="text-yellow-700 text-sm">Extremely volatile, regulatory uncertainty, technology risks, and potential total loss of investment.</p>
            </div>
            <div className="bg-white bg-opacity-60 rounded-lg p-4">
              <h3 className="font-semibold text-yellow-800 mb-2">Options Trading</h3>
              <p className="text-yellow-700 text-sm">Complex strategies, time decay, potential for total premium loss, and unlimited risk on certain positions.</p>
            </div>
            <div className="bg-white bg-opacity-60 rounded-lg p-4">
              <h3 className="font-semibold text-yellow-800 mb-2">Futures Trading</h3>
              <p className="text-yellow-700 text-sm">High leverage, margin requirements, potential for losses exceeding initial investment, and daily mark-to-market.</p>
            </div>
            <div className="bg-white bg-opacity-60 rounded-lg p-4">
              <h3 className="font-semibold text-yellow-800 mb-2">Forex Trading</h3>
              <p className="text-yellow-700 text-sm">Currency fluctuations, geopolitical risks, high leverage, and 24-hour market exposure.</p>
            </div>
          </div>
        </motion.div>

        {/* Risk Management */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8"
        >
          <div className="flex items-center mb-4">
            <InformationCircleIcon className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-blue-900">Risk Management Recommendations</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="font-semibold text-blue-800 mb-2">✓ Use Stop-Loss Orders</h3>
              <p className="text-blue-700 text-sm">Set predetermined exit points to limit potential losses on positions.</p>
            </div>
            <div>
              <h3 className="font-semibold text-blue-800 mb-2">✓ Diversify Your Portfolio</h3>
              <p className="text-blue-700 text-sm">Spread risk across different assets, markets, and strategies.</p>
            </div>
            <div>
              <h3 className="font-semibold text-blue-800 mb-2">✓ Only Risk What You Can Afford</h3>
              <p className="text-blue-700 text-sm">Never invest money you need for essential expenses or cannot afford to lose.</p>
            </div>
            <div>
              <h3 className="font-semibold text-blue-800 mb-2">✓ Educate Yourself</h3>
              <p className="text-blue-700 text-sm">Understand the products you trade and continuously improve your knowledge.</p>
            </div>
          </div>
        </motion.div>

        {/* Regulatory Notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="bg-gray-50 border border-gray-200 rounded-lg p-6 mt-8"
        >
          <div className="flex items-center mb-4">
            <DocumentTextIcon className="h-6 w-6 text-gray-600 mr-3" />
            <h2 className="text-xl font-bold text-gray-900">Regulatory Information</h2>
          </div>
          <div className="space-y-3 text-gray-600">
            <p>
              <strong>Regulatory Oversight:</strong> INTRA CAPITAL LIMITED operates under applicable financial regulations 
              and maintains appropriate licenses for the services we provide.
            </p>
            <p>
              <strong>Investor Protection:</strong> Eligible accounts may be covered by investor protection schemes 
              up to applicable limits. Coverage varies by jurisdiction and account type.
            </p>
            <p>
              <strong>Complaint Procedures:</strong> If you have concerns about our services, please contact our 
              compliance department at compliance@intracapitals.com.
            </p>
          </div>
        </motion.div>

        {/* Acknowledgment */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1 }}
          className="text-center mt-8 p-6 bg-gray-100 border border-gray-300 rounded-lg"
        >
          <h3 className="text-lg font-bold text-gray-900 mb-3">Risk Acknowledgment</h3>
          <p className="text-sm text-gray-700">
            By using our trading services, you acknowledge that you have read, understood, and accepted 
            all the risks outlined in this disclosure. You confirm that you have the financial ability 
            to bear such risks and that trading is suitable for your financial situation and investment objectives.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default RiskDisclosure; 